import {
  keepPreviousData,
  QueryFunctionContext,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { queryKeysAnalytics } from '../queryKeys';

import { fetchSitesDetails } from './fetchSitesDetails';
import { mapSitesDetailsRow } from './mapSitesDetailsRow';
import { SitesDetailsQueryParams, SitesDetailsResponse } from './types';
import { zodSitesDetailsQueryParamsSchema } from './validations';

/**
 * Generates the query options for the sites details query so they can be shared between the useQuery hook and the prefetching.
 *
 * @param queryParams - The query parameters for fetching the sites details.
 * @param options - Additional options for the query.
 * @param ignoreSignal - If true, the abort signal is not passed to the fetch function.
 *   Use this for prefetching in route loaders to prevent request cancellation when the loader completes.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getSitesDetailsQueryOptions = ({
  queryParams,
  options,
  ignoreSignal = false,
}: {
  queryParams: Partial<SitesDetailsQueryParams>;
  options?: Omit<UseQueryOptions<SitesDetailsResponse>, 'queryKey' | 'queryFn'>;
  ignoreSignal?: boolean;
}) => {
  const validatedQueryParams =
    zodSitesDetailsQueryParamsSchema.parse(queryParams);

  const isEnabled = Boolean(
    validatedQueryParams.dateRange.dateFrom &&
    validatedQueryParams.dateRange.dateTo,
  );

  return {
    enabled: isEnabled,
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchSitesDetails(validatedQueryParams, {
        signal: ignoreSignal ? undefined : signal,
      }),
    queryKey: queryKeysAnalytics.getSitesDetails(validatedQueryParams),
    select: mapSitesDetailsRow,
    staleTime: ms('5m'),
    ...options,
  };
};

/**
 * React Query based custom hook for getting the paginated sites details data with given query parameters.
 * Handles pagination and prefetches the next page for better performance.
 *
 * @param queryParams - The query parameters for fetching the sites details.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Site/operation/get_site_activity_table_site_statistics_details_post
 */
export const useQuerySitesDetails = (
  queryParams: Partial<SitesDetailsQueryParams>,
  options?: Omit<
    UseQueryOptions<SitesDetailsResponse, Error, SitesDetailsResponse>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery({
    ...getSitesDetailsQueryOptions({
      options,
      queryParams,
    }),
    placeholderData: keepPreviousData,
  });
